import { useState } from 'react';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Typography,
} from '@mui/material';
import { LoadingButton } from '@mui/lab';
import { useNavigate } from 'react-router-dom';
import { Controller, SubmitHandler, useForm } from 'react-hook-form';
import { useDebounce } from 'use-debounce';
import { classValidatorResolver } from '@hookform/resolvers/class-validator';
import { useExportSyncDraft } from '../../hooks/api/use-sync.hook';
import { LoadingIndicator } from '../LoadingIndicator';
import { ExportFormat } from '../../../../types/requests/sync/export-sync-draft.request';
import { useSearchDomains } from '../../hooks/api/use-domains.hook';
import { ExportForm } from './ExportSyncDraftForm';
import { useSearchOntology } from '../../hooks/api/use-ontology.hook';
import { NodeType } from '../../../../types/constants/node-type';
import { useSearchContracts } from '../../hooks/api/use-contracts.hook';
import { SearchContractsApiRequest } from '../../../../types/requests/contracts/search-contracts.request';

export const ExportSyncDraft = () => {
  const navigate = useNavigate();
  const [contractSearch, setContractSearch] = useState<SearchContractsApiRequest>({});
  const [debouncedContractSearch] = useDebounce(contractSearch, 250);

  const [exportSyncDraft, { loading, error }] = useExportSyncDraft();
  const { loading: loadingDomains, response: domainsResponse, error: domainsError } = useSearchDomains({});
  const { loading: loadingContracts, response: contractsResponse, error: contractsError } = useSearchContracts(debouncedContractSearch);
  const { loading: loadingTopLevelNodes, response: topLevelNodesResponse } = useSearchOntology({
    parentId: null,
    nodeType: NodeType.CATEGORY,
  });

  const {
    control,
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<ExportForm>({
    resolver: classValidatorResolver(ExportForm),
    defaultValues: { format: ExportFormat.FXL },
  });

  const renderContent = () => {
    if (domainsError || contractsError) {
      return <Alert severity="error">{(domainsError || contractsError)?.response?.data?.message}</Alert>;
    }

    return (
      <>
        <Controller
          control={control}
          name="format"
          render={({ field }) => (
            <Autocomplete
              {...register('format')}
              sx={{ mt: '1rem' }}
              onChange={(_event, selectedOptions) => field.onChange(selectedOptions)}
              onBlur={field.onBlur}
              value={field.value}
              options={Object.values(ExportFormat)}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Format"
                  error={Boolean(errors.format)}
                  helperText={errors.format?.message}
                />
              )}
            />
          )}
        />
        <Controller
          control={control}
          name="domains"
          render={({ field }) => (
            <Autocomplete
              {...register('domains', { setValueAs: (value) => value || undefined })}
              loading={loadingDomains}
              sx={{ mt: '1rem' }}
              multiple
              onChange={(_event, selectedOptions) => field.onChange(selectedOptions)}
              onBlur={field.onBlur}
              value={field.value}
              getOptionLabel={({ shortName }) => shortName ?? ''}
              options={domainsResponse?.pages.map(({ result }) => result).flat() ?? []}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Domains"
                  error={Boolean(errors.domains)}
                  helperText={errors.domains?.message}
                />
              )}
            />
          )}
        />
        <Controller
          control={control}
          name="contracts"
          render={({ field }) => (
            <Autocomplete
              {...register('contracts', { setValueAs: (value) => value || undefined })}
              loading={loadingContracts}
              sx={{ mt: '1rem' }}
              multiple
              filterOptions={(x) => x}
              onChange={(_event, selectedOptions) => field.onChange(selectedOptions)}
              onInputChange={(_event, value) => {
                const [district, ea] = value.split('-');
                setContractSearch({ district: district || undefined, ea: ea || undefined });
              }}
              onBlur={field.onBlur}
              value={field.value}
              getOptionLabel={({ district, ea, number }) => `${district}-${ea} (${number})`}
              options={contractsResponse?.pages.map(({ result }) => result).flat() ?? []}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Contracts"
                  error={Boolean(errors.contracts)}
                  helperText={errors.contracts?.message}
                  placeholder="District-EA"
                />
              )}
              renderOption={(props, { district, ea, number, descriptionOfWork, locationDescription }) => (
                <Box component="li" {...props} display="flex" flexDirection="column">
                  <Box width="100%">
                    <Typography>
                      District: {district} | EA: {ea} | Number: {number}
                    </Typography>
                  </Box>
                  <Box width="100%">
                    <Typography variant="caption">{descriptionOfWork}</Typography>
                    <Typography variant="caption">{locationDescription}</Typography>
                  </Box>
                </Box>
              )}
            />
          )}
        />
        <Controller
          control={control}
          name="topLevelNodes"
          render={({ field }) => (
            <Autocomplete
              {...register('topLevelNodes', { setValueAs: (value) => value || undefined })}
              loading={loadingTopLevelNodes}
              sx={{ mt: '1rem' }}
              multiple
              onChange={(_event, selectedOptions) => field.onChange(selectedOptions)}
              onBlur={field.onBlur}
              value={field.value}
              getOptionLabel={({ name }) => name ?? ''}
              options={topLevelNodesResponse?.pages.map(({ result }) => result).flat() ?? []}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Categories"
                  error={Boolean(errors.topLevelNodes)}
                  helperText={errors.topLevelNodes?.message}
                />
              )}
            />
          )}
        />
      </>
    );
  };

  const onSubmit: SubmitHandler<ExportForm> = async ({ format, contracts, domains, topLevelNodes }) => {
    await exportSyncDraft({
      format,
      domainIds: domains?.map(({ id }) => id),
      topLevelNodeIds: topLevelNodes?.map(({ id }) => id),
      contractIds: contracts?.map(({ id }) => id),
    });
    navigate('..');
  };

  return (
    <Dialog open={true}>
      {loading ? (
        <LoadingIndicator />
      ) : (
        <form onSubmit={handleSubmit(onSubmit)}>
          <DialogTitle>Export</DialogTitle>
          <DialogContent sx={{ width: '400px' }}>
            {renderContent()}
            {error ? (
              <Alert sx={{ mt: '1rem' }} severity="error">
                {error?.response?.data?.message}
              </Alert>
            ) : null}
          </DialogContent>
          <DialogActions>
            <Button disabled={isSubmitting} variant="outlined" onClick={() => navigate('..')} color="primary">
              Cancel
            </Button>
            <LoadingButton color="primary" type="submit" variant="contained" loading={isSubmitting}>
              Export
            </LoadingButton>
          </DialogActions>
        </form>
      )}
    </Dialog>
  );
};
